import * as THREE from 'three';
import { InteractiveStar } from './InteractiveStar.js';

const SKY_RADIUS = 800;

/**
 * Convert horizontal sky coordinates (azimuth, altitude in degrees) to a
 * 3D position on the celestial sphere. Used to manually place objects
 * (like the Family Constellation) at chosen positions in the sky rather
 * than at real cataloged star locations.
 */
function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

// Card content for each family member, finalized voice from copy/family-constellation.md
const FATHER_CARD = `
  <h2>Ishwarappa Mattikalli</h2>
  <div class="label">Father</div>
  <p>He is the kind of star that does not pulse and does not flare. He just holds his light. Steady. Warm. Always in the same corner of your sky.</p>
  <p>You have been borrowing his gravity your whole life.</p>
`;

const MOTHER_CARD = `
  <h2>Sheela Haveri</h2>
  <div class="label">Mother</div>
  <p>Astronomers have a name for the kind of star she is: a binary system. Two stars locked in mutual gravity, orbiting each other forever. They are so close that material flows between them - they are constantly pulling on each other, exchanging light, exchanging heat.</p>
  <p>The two stars are not always calm. Sometimes they pull harder than they should. Sometimes the energy passes back and forth and back and forth and neither one rests.</p>
  <p>But here is the thing about a binary system.</p>
  <p>The two stars cannot exist apart. The same gravity that makes them pull on each other is also the thing holding them in the same sky.</p>
  <p>You and your mother are a binary system.</p>
  <p>You always have been. You always will be.</p>
`;

const BROTHER_CARD = `
  <h2>Ganesh Mattikalli</h2>
  <div class="label">Brother</div>
  <p>He is six years younger than you and somehow already a person you choose to spend a Saturday with. That is the rarest thing siblings can become to each other.</p>
  <p>In astronomical terms, he is a companion star - a smaller, brighter point that travels through the same gravity well as you. Same orbit. Same sky. You will never have to look far for him.</p>
  <p>Astronomers also have a phrase for what you two do together. They call it "co-evolution."</p>
  <p>You call it shopping.</p>
`;

/**
 * Create the Family Constellation: 3 members in a small triangle, with the
 * mother rendered as a mass-transfer binary (two close stars pulsing in
 * opposing phases - when one expands the other contracts, suggesting the
 * exchange of energy that defines a real mass-transfer binary).
 *
 * Returns { members, lines, motherStream } - caller adds each to the sky.
 */
export function createFamily({ onClick }) {
  // Triangle in the upper-eastern sky, where the visitor will look as she
  // explores away from where Sirius and Birth Star sit
  const fatherPos = azAlt(80, 65);
  const motherCenter = azAlt(95, 52);
  const brotherPos = azAlt(110, 65);

  // Mother binary: two sprites slightly offset from the triangle's bottom point
  const motherOffset = new THREE.Vector3(10, 0, 0);
  const motherAPos = motherCenter.clone().add(motherOffset);
  const motherBPos = motherCenter.clone().sub(motherOffset);

  const father = new InteractiveStar({
    position: fatherPos,
    color: 0xfff0c8,
    glowColor: '255, 235, 180',
    scale: 38,
    pulseSpeed: 0.4, // very slow - calm and steady
    pulseAmount: 0.025, // barely pulses at all
    discoveryId: 'family-father',
    category: 'people',
    cardHTML: FATHER_CARD,
    onClick,
  });

  // Mother binary - two stars in opposing pulse phases
  const motherA = new InteractiveStar({
    position: motherAPos,
    color: 0xffe0a0,
    glowColor: '255, 220, 160',
    scale: 32,
    pulseSpeed: 1.8, // faster, more intense
    pulseAmount: 0.12,
    initialPhase: 0,
    discoveryId: 'family-mother',
    category: 'people',
    cardHTML: MOTHER_CARD,
    onClick,
  });

  const motherB = new InteractiveStar({
    position: motherBPos,
    color: 0xffd098,
    glowColor: '255, 200, 140',
    scale: 30,
    pulseSpeed: 1.8,
    pulseAmount: 0.12,
    initialPhase: Math.PI, // OPPOSITE phase - when A expands, B contracts
    discoveryId: 'family-mother', // same discovery as motherA
    category: 'people',
    cardHTML: MOTHER_CARD,
    onClick,
  });

  const brother = new InteractiveStar({
    position: brotherPos,
    color: 0xfff5d8,
    glowColor: '255, 240, 200',
    scale: 26, // smaller than father
    pulseSpeed: 1.0,
    pulseAmount: 0.05,
    discoveryId: 'family-brother',
    category: 'people',
    cardHTML: BROTHER_CARD,
    onClick,
  });

  // Faint triangle lines connecting father → mother → brother → father
  const triangleGeometry = new THREE.BufferGeometry();
  triangleGeometry.setFromPoints([
    fatherPos, motherCenter,
    motherCenter, brotherPos,
    brotherPos, fatherPos,
  ]);
  const triangleMaterial = new THREE.LineBasicMaterial({
    color: 0xffd88c,
    transparent: true,
    opacity: 0.15,
    depthWrite: false,
  });
  const lines = new THREE.LineSegments(triangleGeometry, triangleMaterial);

  // Visible mass-transfer line between mother A and B
  const streamGeometry = new THREE.BufferGeometry();
  streamGeometry.setFromPoints([motherAPos, motherBPos]);
  const streamMaterial = new THREE.LineBasicMaterial({
    color: 0xffe0a0,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
  });
  const motherStream = new THREE.LineSegments(streamGeometry, streamMaterial);

  // === Constellation hover glow ===
  // When any family member is hovered, brighten the triangle lines.
  // Wraps each member's onHover/onUnhover to track shared hover state.
  const allMembers = [father, motherA, motherB, brother];
  let familyHoverCount = 0;
  const baseLineOpacity = 0.15;
  const hoveredLineOpacity = 0.55;

  for (const member of allMembers) {
    const origHover = member.onHover.bind(member);
    const origUnhover = member.onUnhover.bind(member);
    member.onHover = function () {
      origHover();
      familyHoverCount++;
      triangleMaterial.opacity = hoveredLineOpacity;
    };
    member.onUnhover = function () {
      origUnhover();
      familyHoverCount = Math.max(0, familyHoverCount - 1);
      if (familyHoverCount === 0) {
        triangleMaterial.opacity = baseLineOpacity;
      }
    };
  }

  return {
    members: allMembers,
    lines,
    motherStream,
  };
}
